'use client';

import { useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import type { PostHogIdentity } from '@/lib/posthog/identity';
import { apiFetch } from '@/lib/utils/api-fetch';

const IDENTITY_URL = '/api/v1/posthog-identity';

// usePostHog() returns the global posthog stub even when no PHProvider is
// mounted (i.e. when NEXT_PUBLIC_POSTHOG_KEY is unset), so guarding on `ph`
// alone isn't enough — we'd still fire a request + Prisma query on every
// layout mount in dev. Gate on the key directly to match the provider.
const POSTHOG_ENABLED = Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY);

// PostHog bills every `$identify`, `$set` and `$groupidentify` like a real
// event, and posthog-js re-sends `$set` on each identify that carries
// properties, even for the distinct id it already holds. The layout mounts on
// every full page load, so this records what was last sent and skips a call
// whose identity and properties are unchanged.
const SENT_STORAGE_KEY = 'hire-go:posthog-identity-sent';

type SentFingerprints = { identify?: unknown; group?: unknown };

// FNV-1a keeps the trait values, email included, out of localStorage.
function fingerprint(value: unknown): string {
  const text = JSON.stringify(value);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

function readSent(): SentFingerprints {
  try {
    const raw = window.localStorage.getItem(SENT_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as SentFingerprints)
      : {};
  } catch {
    return {};
  }
}

function writeSent(sent: SentFingerprints) {
  try {
    window.localStorage.setItem(SENT_STORAGE_KEY, JSON.stringify(sent));
  } catch {
    // Without storage the next page load identifies again, which is correct.
  }
}

export default function PostHogIdentify() {
  const ph = usePostHog();

  useEffect(() => {
    if (!POSTHOG_ENABLED || !ph) {
      return;
    }
    const controller = new AbortController();
    const pending = apiFetch(IDENTITY_URL, {
      signal: controller.signal,
    }) as Promise<PostHogIdentity | null>;

    void pending
      .then((identity: PostHogIdentity | null) => {
        if (!identity) {
          return;
        }

        const sent = readSent();
        const next: SentFingerprints = { ...sent };

        if (identity.kind === 'human') {
          const traits = {
            ...identity.traits,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };
          const identifyPrint = fingerprint([identity.distinctId, traits]);
          // The distinct id check covers a user switch and a PostHog storage
          // reset, where the stored fingerprint alone would be stale.
          if (
            ph.get_distinct_id() !== identity.distinctId ||
            sent.identify !== identifyPrint
          ) {
            ph.identify(identity.distinctId, traits);
            next.identify = identifyPrint;
          }
        }
        // Otherwise: internal user. Skip identify so PostHog doesn't create a
        // ghost person profile (mirrors $process_person_profile: false on the
        // server). Events still attribute via the `account` group below.

        // posthog-js persists `$groups`, so a skipped call keeps attributing
        // events to the account.
        const groupPrint = fingerprint([
          identity.accountExternalId,
          identity.groupProperties,
        ]);
        if (
          ph.getGroups().account !== identity.accountExternalId ||
          sent.group !== groupPrint
        ) {
          ph.group(
            'account',
            identity.accountExternalId,
            identity.groupProperties
          );
          next.group = groupPrint;
        }

        writeSent(next);
      })
      // An abort or a dropped connection rejects, and an unhandled rejection
      // is a console error on every unmount. `apiFetch` has already logged a
      // real failure, and identity is context, never a reason to fail a page.
      .catch(() => {});

    return () => {
      controller.abort();
    };
  }, [ph]);

  return null;
}
